// src/components/workflow/ApproverReviewView.jsx
import React, { useState } from "react";
import { FieldRenderer } from "../fields/FieldRenderer";
import { CanvasPageRenderer } from "../document/CanvasPageRenderer";
import { downloadPDF } from "../onboardingUtils/pdfGenerator";
import { A4_WIDTH, A4_HEIGHT } from "../constants/layoutConstants";
import Button from "../../Buttons/Button";
import { downloadFilledPDF } from "../onboardingUtils/pdfGenerator";

export const ApproverReviewView = ({
  document,
  approverId,
  selectedApplicant,
  onSelectApplicant,
  applicants = [],
  userFieldData = {},
  initiatorData = {},
  applicantData = {},
  onUpdateField,
  onApprove,
  onReject,
  // onSave,
  onBack,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [remarks, setRemarks] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Approver editable fields on current page
  const allFields = document.droppedFields?.[currentPage] || [];

  // Separate fields by role
  const initiatorFields = allFields.filter((f) => f.role === "initiator");
  const applicantFields = allFields.filter((f) => f.role === "applicant");
  const approverFields = allFields.filter((f) => f.role === "approver");

  const validateFields = () => {
    const errors = [];
    approverFields.forEach((field) => {
      if (field.required) {
        const value = userFieldData?.[field.id];
        if (!value || String(value).trim() === "") {
          errors.push({
            fieldId: field.id,
            label: field.label || "Unnamed Field",
            page: field.page,
          });
        }
      }
    });
    return errors;
  };

  const handleSave = () => {
    const errors = validateFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      alert(
        "Please fill all required approver fields before saving:\n" +
          errors
            .slice(0, 5)
            .map((e) => `${e.label} (Page ${e.page})`)
            .join("\n")
      );
      if (errors[0]?.page) {
        setCurrentPage(errors[0].page);
      }
      return false;
    }
    setValidationErrors([]);
    return true;
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Collect all data: initiator + applicant + approver
      const allFieldValues = {
        ...initiatorData,
        ...applicantData,
        ...userFieldData,
      };

      // Filter out metadata fields (status, role, timestamps)
      const cleanedData = {};
      Object.entries(allFieldValues).forEach(([key, value]) => {
        if (
          !key.startsWith("_") &&
          ![
            "status",
            "role",
            "submittedAt",
            "createdAt",
            "approvedAt",
          ].includes(key)
        ) {
          cleanedData[key] = value;
        }
      });

      // Pass the cleaned data structure
      await downloadFilledPDF(
        document,
        cleanedData,
        `${selectedApplicant}_approved`
      );
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download document: " + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleApprove = () => {
    // Check if all required approver fields are filled
    const missingApproverFields = approverFields
      .filter((f) => f.required)
      .filter(
        (f) => !userFieldData?.[f.id] || !String(userFieldData[f.id]).trim()
      );

    if (missingApproverFields.length > 0) {
      alert(
        `Please fill all required approver fields:\n- ${missingApproverFields
          .map((f) => f.label)
          .join("\n- ")}`
      );
      return;
    }

    if (remarks.trim() === "") {
      alert("Please add approval remarks");
      return;
    }

    // Store remarks
    onUpdateField("_approver_remarks", remarks);
    onApprove(selectedApplicant);
  };

  const handleReject = () => {
    if (remarks.trim() === "") {
      alert("Please add rejection reason");
      return;
    }
    // Store remarks
    onUpdateField("_approver_remarks", remarks);
    onReject(selectedApplicant);
  };

  return (
    <div className="flex flex-1 bg-gray-100">
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button
              type="icon"
              icon={"chevron_left"}
              variant="text"
              color="black"
              onClick={onBack}
            />
            <div>
              <h2 className="text-xl font-semibold">{document.documentName}</h2>
              <p className="text-sm text-gray-500">
                Approver Review - {approverId}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              title={isDownloading ? "Downloading..." : "Download"}
              onClick={handleDownload}
              disabled={isDownloading}
              icon={"download"}
            />

            <Button title={"Reject"} onClick={handleReject} icon={"close"} />
            <Button title={"Approve"} onClick={handleApprove} icon={"check"} />
          </div>
        </div>

        {/* Applicant selector */}
        <div className="flex items-center gap-3 px-6 mb-4">
          <label className="text-sm font-medium" htmlFor="applicant-select">
            Reviewing Applicant:
          </label>
          <select
            id="applicant-select"
            value={selectedApplicant}
            onChange={(e) => {
              onSelectApplicant(e.target.value);
              setRemarks("");
              setValidationErrors([]);
            }}
            className="px-3 py-2 border rounded-lg"
          >
            {applicants.map((app) => (
              <option key={app} value={app}>
                {app}
              </option>
            ))}
          </select>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-auto p-8 flex flex-col items-center">
            <div className="max-w-4xl w-full">
              {/* Page navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  icon={"chevron_left"}
                  type="icon"
                />
                <span className="text-sm font-medium">
                  Page {currentPage} of {document.pageCount || 1}
                </span>

                <Button
                  type="icon"
                  icon={"chevron_right"}
                  variant="text"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(document.pageCount || 1, prev + 1)
                    )
                  }
                  disabled={currentPage === (document.pageCount || 1)}
                />
              </div>

              {/* PDF and fields layer */}
              <div
                className="relative bg-white shadow-lg"
                style={{ width: `${A4_WIDTH}px`, height: `${A4_HEIGHT}px` }}
              >
                {/* PDF Background */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                >
                  <CanvasPageRenderer
                    arrayBuffer={document.arrayBuffer}
                    pageNumber={currentPage}
                    width={A4_WIDTH}
                    height={A4_HEIGHT}
                    isBlankDocument={document.isBlankDocument}
                  />
                </div>

                {/* Initiator fields (read-only) */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    pointerEvents: "none",
                    opacity: 0.7,
                  }}
                >
                  {initiatorFields.map((field) => (
                    <FieldRenderer
                      key={field.id}
                      field={field}
                      value={initiatorData?.[field.id] || ""}
                      readOnly={true}
                      onChange={() => {}}
                      role="initiator"
                    />
                  ))}
                </div>

                {/* Applicant fields (read-only) */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 15,
                    pointerEvents: "none",
                    opacity: 0.85,
                  }}
                >
                  {applicantFields.map((field) => (
                    <FieldRenderer
                      key={field.id}
                      field={field}
                      value={applicantData?.[field.id] || ""}
                      readOnly={true}
                      onChange={() => {}}
                      role="applicant"
                    />
                  ))}
                </div>

                {/* Approver fields (editable) */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 20,
                  }}
                >
                  {approverFields.map((field) => {
                    const hasError = validationErrors.some(
                      (e) => e.fieldId === field.id
                    );
                    return (
                      <div
                        key={field.id}
                        className={
                          hasError ? "ring-2 ring-red-500 rounded" : ""
                        }
                      >
                        <FieldRenderer
                          field={field}
                          value={userFieldData?.[field.id] || ""}
                          onChange={(val) => {
                            onUpdateField(field.id, val);
                            setValidationErrors((prev) =>
                              prev.filter((e) => e.fieldId !== field.id)
                            );
                          }}
                          readOnly={false}
                          role="approver"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Remarks sidebar */}
          <div className="w-80 bg-white border-l p-4 flex flex-col gap-4">
            <h3 className="font-semibold text-gray-900 mb-2">Remarks</h3>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add approval remarks or rejection reason..."
              className="w-full h-40 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-info-500 resize-none"
            />
            <div className="space-y-4 overflow-y-auto">
              {/* Initiator fields summary */}
              {initiatorFields.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">
                    Initiator Fields
                  </h4>
                  <div className="space-y-2">
                    {initiatorFields.map((field) => (
                      <div
                        key={field.id}
                        className="p-2 bg-success-50 border border-success-200 rounded text-xs"
                      >
                        <p className="font-medium">{field.label}</p>
                        <p className="text-gray-600">
                          {initiatorData?.[field.id] || "(empty)"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applicant fields summary */}
              {applicantFields.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">
                    Applicant Fields
                  </h4>
                  <div className="space-y-2">
                    {applicantFields.map((field) => (
                      <div
                        key={field.id}
                        className="p-2 bg-info-50 border border-nfo-200 rounded text-xs"
                      >
                        <p className="font-medium">{field.label}</p>
                        <p className="text-gray-600">
                          {applicantData?.[field.id] || "(empty)"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approver fields summary */}
              {approverFields.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">
                    Approver Fields
                  </h4>
                  <div className="space-y-2">
                    {approverFields.map((field) => (
                      <div
                        key={field.id}
                        className="p-2 bg-leave-50 border border-leave-200 rounded text-xs"
                      >
                        <p className="font-medium">
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </p>
                        <p className="text-gray-600">
                          {userFieldData?.[field.id] || "(empty)"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Applicant info */}
            <div className="pt-4 border-t">
              <h4 className="font-semibold text-sm text-gray-700 mb-2">
                Applicant
              </h4>
              <p className="text-sm text-gray-600">{selectedApplicant}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
